import { Geometry, LineString, Point, Polygon} from 'ol/geom';
import { Vector as VectorLayer, VectorImage} from 'ol/layer';
import { Vector as VectorSource, Cluster } from 'ol/source';
import { Style, Fill, Stroke,Text, Icon, Circle as CircleStyle } from 'ol/style';
import { asArray as colorAsArray, asString as colorAsString } from 'ol/color';
import { Feature } from 'ol';
import { ICONS, OBJECT_TYPE, OBJECT_ID } from '../constants';
import { FeatureType, PathDirection } from '../enums';
import { COLORS } from '../constants';
import { transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';

const DEFAULT_ICON_FONT = 'interRegular';
const DEFAULT_PATH_FONT = 'interRegular';
const DEFAULT_PATH_ICON_SIZE = 44;
const DEFAULT_POI_ICON_SIZE = 28;
const ICON_SELECTION_PADDING = 4;
const CLOSED_TEXT = 'Stängd';
let renderScale = 0.5;

/**
 * Gets the render scale variable
 */
const getRenderScale = (): number => {
    return renderScale;
};

/**
 * Sets the render scale variable
 */
const setRenderScale = (scale: number) => {
    renderScale = scale;
};

export const getDirectionArrowSpacing = (zoom?: number): number => {
    const safeZoom = Number.isFinite(zoom) ? Math.max(0, Math.min(Number(zoom), 22)) : 12;

    // A long trail at low zoom should read as a clean line, not a comb. Hide the
    // direction arrows until the user zooms in enough to benefit from them.
    if (safeZoom < 10) return 12000;
    if (safeZoom < 12) return 9000;
    if (safeZoom < 14) return 6000;
    if (safeZoom < 16) return 4000;
    if (safeZoom < 18) return 2500;
    if (safeZoom < 20) return 1500;
    return 900;
};

// Cache for colored icon data URLs
const iconColorCache: {[key: string]: string} = {};

/**
 * Creates a colored icon on a white circular background.
 * Takes a white PNG icon, tints it to the specified color,
 * and places it on a white circle backdrop.
 */
const getColoredIconDataUrl = (imageSrc: string, color: string): string => {
    const cacheKey = `${imageSrc}_${color}`;
    
    // Return cached version if available
    if (iconColorCache[cacheKey]) {
        return iconColorCache[cacheKey];
    }
    
    // If not in cache, return original (it will be cached asynchronously)
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
        // Add padding around the icon for the circular background
        const padding = 10;
        const size = Math.max(img.width, img.height) + padding * 2;
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) return;
        
        canvas.width = size;
        canvas.height = size;
        
        // Draw white circle background
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Create a temporary canvas to tint the icon
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return;
        
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        tempCtx.drawImage(img, 0, 0);
        
        // Get image data and tint non-transparent pixels
        const imageData = tempCtx.getImageData(0, 0, img.width, img.height);
        const data = imageData.data;
        
        const colorArray = colorAsArray(color);
        const r = colorArray[0];
        const g = colorArray[1];
        const b = colorArray[2];
        
        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha > 0) {
                data[i] = r;
                data[i + 1] = g;
                data[i + 2] = b;
            }
        }
        
        tempCtx.putImageData(imageData, 0, 0);
        
        // Draw the tinted icon centered on the white circle
        const offsetX = (size - img.width) / 2;
        const offsetY = (size - img.height) / 2;
        ctx.drawImage(tempCanvas, offsetX, offsetY);
        
        // Cache the data URL
        iconColorCache[cacheKey] = canvas.toDataURL();
    };
    
    img.src = imageSrc;
    
    // Return original for first render
    return imageSrc;
};

/**
 * Creates a empty vector source
 */
const createEmptyVectorSource = (): VectorSource<Feature<Geometry>> => {
    return new VectorSource<Feature<Geometry>>({
        features: [],
    });
};

/**
 * Creates a empty vector layer for a source
 */
const createEmptyVectorLayer = (
    source: VectorSource<Feature<Geometry>>,
    styleFunction?: (feature: any, resolution: number) => any,
    renderBuffer?: number,
): VectorImage => {
    return new VectorImage({
        source,
        ...(styleFunction && { style: styleFunction }),
        ...(renderBuffer !== undefined && { renderBuffer }),
    });
};

/**
 * Creates a cluster layer for the given vector source using the given icon path and color
 */
const createIconClusterLayer = (source: VectorSource<Feature<Geometry>>, path: string, color: string): VectorImage => {
    const cluster = new Cluster({
        distance: 40,
        source,
    });

    return new VectorImage({
        source: cluster,
        style: (feature) => {
            const size = feature.get('features').length;

            if (size === 1) {
                return feature.get('features')[0]['style_'];
            }

            return createBaseMapIconStyle(path, `(${size})`, color, 30, DEFAULT_ICON_FONT, 45, DEFAULT_PATH_ICON_SIZE);
        },
    });
};

/**
 * Creates a set of base styles for a icon
 */
const createBaseMapIconStyle = (
    iconPath: string,
    name: string | undefined,
    color: string,
    fontSize: number,
    fontFamily: string,
    textOffset: number,
    backplateSize: number,
    inverted: boolean = true,
    opacity: number = 1,
    tintIcon: boolean = true,
): Style[] => {
    return [
        // White circle backplate
        new Style({
            image: createCircleBackingPlateStyle(backplateSize, opacity),
        }),
        // Text label
        new Style({
            text: new Text({
                text: name,
                font: `${fontSize}px ${fontFamily}`,
                offsetY: textOffset * renderScale,
                scale: renderScale,
                stroke: new Stroke({
                    color: inverted ? '#000000' : '#FFFFFF',
                    width: 8,
                }),
                fill: new Fill({
                    color: inverted ? '#FFFFFF' : color,
                }),
            }),
        }),
        // Icon
        new Style({
            image: new Icon({
                src: iconPath,
                scale: renderScale,
                crossOrigin: 'anonymous',
                ...(tintIcon ? { color } : {}),
                opacity,
            }),
        }),
    ];
};

/**
 * Creates a circle style, mainly used behind icons
 */
const createCircleBackingPlateStyle = (radius: number = 0, opacity: number = 1): CircleStyle => {
    return new CircleStyle({
        radius: radius * renderScale,
            fill: new Fill({
            color: colorWithAlpha('#FFFFFF', opacity),
        }),
    });
};

/**
 * Creates a new color using the given hex color string and a alpha value
 */
const colorWithAlpha = (color: string, alpha: number): string => {
    const [r, g, b] = Array.from(colorAsArray(color));
    return colorAsString([r, g, b, alpha]);
};

/**
 * Creates a base feature for a icon using the given style
 */
const createBaseMapIconFeature = (
    point: Point,
    style: Style | Style[],
    type?: string,
    id?: string,
): Feature<Geometry> => {
    const feature = new Feature<Geometry>({
        geometry: point,
    });
    // Store style as property instead of setting it directly
    // This allows layer-level style functions to control rendering based on zoom
    feature.set('__baseStyle', style);
    feature.set(OBJECT_TYPE, type);
    feature.set(OBJECT_ID, id);
    return feature;
};

/**
 * Creates a styled feature for a gps indicator icon
 */
const createGpsIndicatorFeature = (point: Point, heading?: number): Feature<Geometry> => {
    const feature = new Feature({
        geometry: point,
    });

    const style = new Style({
        image: new Icon({
            src: ICONS.GPS_POSITION,
            scale: renderScale,
            rotation: heading ?? 0,
        }),
    });
    feature.setStyle(style);
    return feature;
};

/**
 * Creates a styled feature for a snowmobile path icon
 */
const createSnowmobilingIconFeature = (
    point: Point,
    name: string | undefined,
    isBold: boolean = false,
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            ICONS.PATHS.SNOWMOBILING,
            name,
            COLORS.PATHS.SNOWMOBILING,
            32,
            DEFAULT_ICON_FONT,
            64,
            isBold ? DEFAULT_PATH_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_PATH_ICON_SIZE,
            false,
        ),
    );
};

/**
 * Creates a styled feature for a common path icon
 */
const createCommonPathIconFeature = (
    point: Point,
    id: string,
    name: string | undefined,
    color: string,
    iconPath: string,
    isBold: boolean = false
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            iconPath,
            name,
            color,
            32,
            DEFAULT_ICON_FONT,
            64,
            isBold ? DEFAULT_PATH_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_PATH_ICON_SIZE,
            false,
        ),
        FeatureType.Path,
        id,
    );
};

/**
 * Creates a styled feature for a common poi icon
 */
const createCommonPoiIconFeature = (
    point: Point,
    id: string,
    name: string | undefined,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
    textOffset: number = 42,
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            iconPath,
            name,
            color,
            24,
            DEFAULT_ICON_FONT,
            textOffset,
            isBold ? DEFAULT_POI_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_POI_ICON_SIZE,
            inverted,
            1,
            iconPath.endsWith('.png'),
        ),
        FeatureType.Poi,
        id,
    );
};

// Layout of events grouped below their POI, in unscaled units (multiplied by renderScale)
const POI_EVENT_ROW_OFFSET_Y = 140;
const POI_EVENT_CONNECTOR_START_Y = 78;
const POI_EVENT_ROW_FIRST_X = 75;
const POI_EVENT_ROW_SPACING = 130;
const POI_EVENT_ICON_SCALE = 1.1;
const POI_EVENT_GROUP_PROPERTY = '__poiEventGroup';
// Grouped events are drawn far from their geometry; OpenLayers only renders and hit-tests
// features whose geometry lies within this many pixels of the viewport/click.
const POI_LAYER_RENDER_BUFFER = 400;

type PoiEventGroupRole = 'connector' | 'row' | 'badge';

const getPoiEventRowOffset = (index: number): [number, number] => [
    POI_EVENT_ROW_FIRST_X + index * POI_EVENT_ROW_SPACING,
    POI_EVENT_ROW_OFFSET_Y,
];

/**
 * Creates the line connecting a POI with the row of events below it
 */
const createPoiEventConnectorFeature = (point: Point, eventCount: number, color: string): Feature<Geometry> => {
    const [lastX, rowY] = getPoiEventRowOffset(Math.max(0, eventCount - 1));
    const style = new Style({
        renderer: (coordinates, state) => {
            const [x, y] = coordinates as number[];
            const scale = state.pixelRatio * renderScale;
            const ctx = state.context;
            ctx.save();
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(x, y + POI_EVENT_CONNECTOR_START_Y * scale);
            ctx.lineTo(x, y + rowY * scale);
            ctx.lineTo(x + lastX * scale, y + rowY * scale);
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 10 * scale;
            ctx.stroke();
            ctx.strokeStyle = color;
            ctx.lineWidth = 4 * scale;
            ctx.stroke();
            ctx.restore();
        },
        hitDetectionRenderer: () => undefined,
        // Custom renderers are drawn after icons within the same zIndex; keep the line underneath
        zIndex: -1,
    });
    const feature = createBaseMapIconFeature(point, style);
    feature.set(POI_EVENT_GROUP_PROPERTY, 'connector' as PoiEventGroupRole);
    return feature;
};

/**
 * Creates an event icon placed in the row below its POI. The feature keeps the POI
 * coordinate and is shifted in screen pixels so the layout is independent of zoom.
 */
const createPoiEventRowIconFeature = (
    point: Point,
    index: number,
    id: string,
    label: string,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
    withGroupRole: boolean = true,
): Feature<Geometry> => {
    const [x, y] = getPoiEventRowOffset(index);
    const displacement = [x * renderScale, -y * renderScale];
    const backplateSize = (isBold ? DEFAULT_POI_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_POI_ICON_SIZE) * POI_EVENT_ICON_SCALE;
    const styles = [
        new Style({
            image: new CircleStyle({
                radius: backplateSize * renderScale,
                fill: new Fill({ color: '#FFFFFF' }),
                displacement,
            }),
        }),
        new Style({
            text: new Text({
                text: label,
                font: `${isBold ? 'bold ' : ''}22px ${DEFAULT_ICON_FONT}`,
                offsetX: x * renderScale,
                offsetY: (y + 54) * renderScale,
                scale: renderScale,
                textAlign: 'center',
                stroke: new Stroke({
                    color: '#000000',
                    width: 5,
                }),
                fill: new Fill({
                    color: '#FFFFFF',
                }),
            }),
        }),
        new Style({
            image: new Icon({
                src: iconPath,
                scale: renderScale * POI_EVENT_ICON_SCALE,
                crossOrigin: 'anonymous',
                ...(iconPath.endsWith('.png') ? { color } : {}),
                displacement,
            }),
        }),
    ];
    const feature = createBaseMapIconFeature(point, styles, FeatureType.Poi, id);
    if (withGroupRole) {
        feature.set(POI_EVENT_GROUP_PROPERTY, 'row' as PoiEventGroupRole);
    }
    return feature;
};

/**
 * Creates a small count badge on a POI, shown when its event row is hidden
 */
const createPoiEventCountBadgeFeature = (point: Point, poiId: string, count: number, color: string): Feature<Geometry> => {
    const offset = 22 * renderScale;
    const style = [
        new Style({
            image: new CircleStyle({
                radius: 15 * renderScale,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#FFFFFF', width: 4 * renderScale }),
                displacement: [offset, offset],
            }),
        }),
        new Style({
            text: new Text({
                text: `${count}`,
                font: `bold 20px ${DEFAULT_ICON_FONT}`,
                offsetX: offset,
                offsetY: -offset,
                scale: renderScale,
                fill: new Fill({ color: '#FFFFFF' }),
            }),
        }),
    ];
    const feature = createBaseMapIconFeature(point, style, FeatureType.Poi, poiId);
    feature.set(POI_EVENT_GROUP_PROPERTY, 'badge' as PoiEventGroupRole);
    return feature;
};

/**
 * Creates a styled feature for a temperature poi icon with dynamic temperature text
 */
const createTemperaturePoiIconFeature = (
    point: Point,
    id: string,
    temperature: number,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
): Feature<Geometry> => {
    // Format temperature with one decimal place and add degree symbol
    const tempText = `${temperature.toFixed(1)}°`;
    
    // Sized in CSS pixels to match the POI icons (backplate diameter 2 * DEFAULT_POI_ICON_SIZE * renderScale)
    const font = `bold ${30 * renderScale}px ${DEFAULT_ICON_FONT}, sans-serif`;
    const padding = 14 * renderScale;
    const boxHeight = 2 * DEFAULT_POI_ICON_SIZE * renderScale;
    const radius = boxHeight / 2;

    const measureCtx = document.createElement('canvas').getContext('2d')!;
    measureCtx.font = font;
    const boxWidth = Math.ceil(Math.max(measureCtx.measureText(tempText).width + padding * 2, boxHeight));

    // Draw at device resolution so the label stays sharp on high-density screens
    const pixelRatio = Math.max(1, window.devicePixelRatio || 1);
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(boxWidth * pixelRatio);
    canvas.height = Math.ceil(boxHeight * pixelRatio);
    const ctx = canvas.getContext('2d')!;
    ctx.scale(pixelRatio, pixelRatio);

    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
    ctx.lineWidth = 1;

    ctx.beginPath();
    const inset = 0.5;
    const r = radius - inset;
    const right = boxWidth - inset;
    const bottom = boxHeight - inset;
    ctx.moveTo(inset + r, inset);
    ctx.lineTo(right - r, inset);
    ctx.arcTo(right, inset, right, inset + r, r);
    ctx.lineTo(right, bottom - r);
    ctx.arcTo(right, bottom, right - r, bottom, r);
    ctx.lineTo(inset + r, bottom);
    ctx.arcTo(inset, bottom, inset, bottom - r, r);
    ctx.lineTo(inset, inset + r);
    ctx.arcTo(inset, inset, inset + r, inset, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw temperature text centered
    ctx.fillStyle = '#1F2933';
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(tempText, boxWidth / 2, boxHeight / 2 + 0.5);

    return createBaseMapIconFeature(
        point,
        [
            new Style({
                image: new Icon({
                    img: canvas,
                    scale: 1 / pixelRatio,
                    anchor: [0.5, 0.5],
                    opacity: 1,
                }),
            }),
        ],
        FeatureType.Poi,
        id,
    );
};

/**
 * Creates a set of base styles for a temperature icon with text overlay
 */
const createTemperatureIconStyle = (
    iconPath: string,
    temperatureText: string,
    color: string,
    fontSize: number,
    fontFamily: string,
    textOffset: number,
    backplateSize: number,
    inverted: boolean = true,
    opacity: number = 1,
): Style[] => {
    // Calculate dimensions for the rounded rectangle background
    const padding = 4 * renderScale; // Reduced padding for tighter layout
    const iconSize = 36 * renderScale;
    const textWidth = temperatureText.length * 14 * renderScale; // Approximate text width (adjusted for decimal)
    const boxWidth = iconSize + textWidth + padding * 2.5;
    const boxHeight = 44 * renderScale;
    
    // Create canvas for rounded rectangle with icon and text
    const canvas = document.createElement('canvas');
    canvas.width = boxWidth;
    canvas.height = boxHeight;
    const ctx = canvas.getContext('2d')!;
    
    // Draw rounded rectangle background
    const radius = 12 * renderScale;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(boxWidth - radius, 0);
    ctx.arcTo(boxWidth, 0, boxWidth, radius, radius);
    ctx.lineTo(boxWidth, boxHeight - radius);
    ctx.arcTo(boxWidth, boxHeight, boxWidth - radius, boxHeight, radius);
    ctx.lineTo(radius, boxHeight);
    ctx.arcTo(0, boxHeight, 0, boxHeight - radius, radius);
    ctx.lineTo(0, radius);
    ctx.arcTo(0, 0, radius, 0, radius);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw temperature text (right side)
    ctx.fillStyle = '#2C3E50'; // Dark blue-gray color
    ctx.font = `bold ${fontSize * renderScale}px ${fontFamily}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(temperatureText, iconSize + padding * 2, boxHeight / 2);
    
    return [
        // Background with icon and text
        new Style({
            image: new Icon({
                img: canvas,
                scale: 1 / renderScale,
                anchor: [0.5, 0.5],
                opacity,
            }),
        }),
        // Thermometer icon (left side)
        new Style({
            image: new Icon({
                src: iconPath,
                scale: 1.0 * renderScale,
                crossOrigin: 'anonymous',
                color: '#2C3E50',
                anchor: [1.8, 0.5], // Position icon on left side of box
                opacity,
            }),
        }),
    ];
};

/**
 * Creates a styled feature for a common aoi icon
 */
const createCommonAoiIconFeature = (
    point: Point,
    id: string,
    name: string | undefined,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            iconPath,
            name,
            color,
            24,
            DEFAULT_ICON_FONT,
            42,
            isBold ? DEFAULT_POI_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_POI_ICON_SIZE,
            inverted,
        ),
        FeatureType.Aoi,
        id,
    );
};

/**
 * Creates a styled feature for a path endcap icon
 */
const createEndcapIconFeature = (
    point: Point, 
    name: string | undefined, 
    inverted: boolean, 
    color: string = COLORS.PATHS.ENDCAP,
    opacity: number = 1
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            ICONS.PATHS.ENDCAP,
            name,
            color,
            20,
            DEFAULT_ICON_FONT,
            30,
            12,
            inverted,
            opacity,
        ),
    );
};

/**
 * Creates a set of base styles for paths
 */
const createBasePathStyles = (
    color: string,
    opacity: number,
    width: number = 6,
    outline: number = 8,
): Style[] => {
    return [
        new Style({
            stroke: new Stroke({
                color: colorWithAlpha('#FFFFFF', opacity), // Apply opacity to white outline too
                width: (width + outline) * renderScale,
            }),
        }),
        new Style({
            stroke: new Stroke({
                color: colorWithAlpha(color, opacity),
                width: width * renderScale,
            }),
        }),
    ];
};

/**
 * Creates a set of base styles for aois
 */
const createBaseAoisStyles = (
    color: string,
    opacity: number = 0.8,
    width: number = 6,
    outline: number = 8,
): Style[] => {
    return [
        new Style({
            stroke: new Stroke({
              color: color,
              width: (width + outline) * renderScale,
            }),
          }),
          new Style({
            fill: new Fill({color: colorWithAlpha(color, opacity)}),
          }),
    ];
};

/**
 * Creates a base text style for paths
 */
const createBasePathTextStyle = (color: string, name?: string): Style => {
    return new Style({
        text: new Text({
            text: name,
            font: `20px ${DEFAULT_PATH_FONT}`,
            scale: renderScale,
            stroke: new Stroke({
                color: '#FFFFFF',
                width: 8,
            }),
            fill: new Fill({
                color,
            }),
        }),
    });
};

/**
 * Creates a set of styled base featureas for a path using the given style
 */
const createBasePathFeatures = (
    coordinates: number[][],
    type: string,
    id: string,
    texts: {color: string, name: string, position: number}[],
    pathStyle: Style | Style[],
    pathDirection?: PathDirection,
    zoom?: number,
): Feature<Geometry>[] => {
    const features = [];
    const pathFeature = new Feature({
        geometry: new LineString(coordinates, 'XYM'),
    });
    pathFeature.set(OBJECT_TYPE, type);
    pathFeature.set(OBJECT_ID, id);
    pathFeature.setStyle(pathStyle);
    features.push(pathFeature);

    // Place arrows along path
    if (pathDirection !== undefined) {
        const absArrowSpacing = getDirectionArrowSpacing(zoom);
        let traversedSpace = 0;
        coordinates.forEach((current, index) => {
            if (index > 0) {
                const previous = coordinates[index - 1];
        
                const projection = 'EPSG:4326';
                const sourceProj = 'EPSG:3857';
                const c1 = transform(previous, sourceProj, projection);
                const c2 = transform(current, sourceProj, projection);
                const dist = getDistance(c1, c2);

                // Check if we've gone long enough along path without an arrow
                if (Math.ceil(traversedSpace / absArrowSpacing) < Math.ceil((traversedSpace + dist) / absArrowSpacing)) {
                    const angOfs = pathDirection === PathDirection.Reverse ? Math.PI : 0;
                    // The direction arrow PNG points upward, i.e. its "forward" direction
                    // is screen-up (north). OpenLayers rotation is positive clockwise.
                    // Math.atan2(dy,dx) gives the map-space angle from the +x axis (east),
                    // counter-clockwise positive. To convert to OL rotation for an
                    // upward-pointing icon: rotation = π/2 − atan2(dy, dx).
                    const rotation = (Math.PI / 2) - Math.atan2(current[1] - previous[1], current[0] - previous[0]) + angOfs;
                    const arrowFeature = new Feature({
                        geometry: new Point(current),
                    });

                    const arrowStyle = new Style({
                        image: new Icon({
                            src: ICONS.DIRECTION_ARROW,
                            scale: renderScale,
                            rotation,
                            // rotate around image center
                            anchor: [0.5, 0.5],
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            rotateWithView: false,
                        }),
                    });
                    arrowFeature.setStyle(arrowStyle);
    
                    features.push(arrowFeature);
                }
                traversedSpace += dist;
            }
        });
    }
    
    texts.forEach(text => {
        const index = Math.floor(coordinates.length * text.position);
        const textFeature = new Feature({
            geometry: new Point(coordinates[index]),
        });
        textFeature.setStyle(createBasePathTextStyle(text.color, text.name));
        features.push(textFeature);
    });
    
    return features;
};

/**
 * Creates icon features along a path at regular intervals, centered on the
 * midpoint of the path. The first icon is always placed at the middle of the
 * trail, then additional icons are placed every `iconSpacing` meters outward
 * in both directions toward start and end.
 */
const createPathIconFeatures = (
    coordinates: number[][],
    iconPath: string,
    color: string,
    iconSpacing: number = 250, // meters between icons
    opacity: number = 1,
): Feature<Geometry>[] => {
    if (coordinates.length < 2) return [];

    const projection = 'EPSG:4326';
    const sourceProj = 'EPSG:3857';

    // --- Step 1: Build a cumulative distance array along the path ---
    const segmentDistances: number[] = [0]; // cumulative distance at each coordinate index
    for (let i = 1; i < coordinates.length; i++) {
        const c1 = transform(coordinates[i - 1], sourceProj, projection);
        const c2 = transform(coordinates[i], sourceProj, projection);
        segmentDistances.push(segmentDistances[i - 1] + getDistance(c1, c2));
    }
    const totalLength = segmentDistances[segmentDistances.length - 1];
    if (totalLength === 0) return [];

    // --- Step 2: Calculate target distances, starting from center ---
    const midDistance = totalLength / 2;
    const targetDistances: number[] = [midDistance];

    // Spread outward from center in both directions
    for (let offset = iconSpacing; offset < totalLength; offset += iconSpacing) {
        const before = midDistance - offset;
        const after = midDistance + offset;
        if (before >= 0) targetDistances.push(before);
        if (after <= totalLength) targetDistances.push(after);
    }

    targetDistances.sort((a, b) => a - b);

    // --- Step 3: For each target distance, find the coordinate on the path ---
    const features: Feature<Geometry>[] = [];

    for (const targetDist of targetDistances) {
        // Find which segment this distance falls on
        let segIndex = 1;
        while (segIndex < segmentDistances.length - 1 && segmentDistances[segIndex] < targetDist) {
            segIndex++;
        }

        // Interpolate between coordinates[segIndex-1] and coordinates[segIndex]
        const segStart = segmentDistances[segIndex - 1];
        const segEnd = segmentDistances[segIndex];
        const segLength = segEnd - segStart;
        const t = segLength > 0 ? (targetDist - segStart) / segLength : 0;

        const x = coordinates[segIndex - 1][0] + t * (coordinates[segIndex][0] - coordinates[segIndex - 1][0]);
        const y = coordinates[segIndex - 1][1] + t * (coordinates[segIndex][1] - coordinates[segIndex - 1][1]);

        const iconFeature = new Feature({
            geometry: new Point([x, y]),
        });

        iconFeature.set('iconSrc', iconPath);
        iconFeature.set('iconColor', color);
        iconFeature.set('iconScale', renderScale * 0.7);
        iconFeature.set('iconAnchor', [0.5, 0.5]);
        iconFeature.set('iconAnchorUnits', 'fraction');
        iconFeature.set('iconOpacity', opacity);

        features.push(iconFeature);
    }

    return features;
};

/**
 * Creates a set of styled features for a snowmobile path
 */
const createSnowmobilePathFeatures = (
    coordinates: number[][],
    id: string,
    isClosed: boolean,
    isBold: boolean,
): Feature<Geometry>[] => {
    return createBasePathFeatures(
        coordinates,
        FeatureType.SnowmobileSubPath,
        id,
        isClosed
            ? [{name: CLOSED_TEXT, position: 0.5, color: COLORS.PATHS.SNOWMOBILING}]
            : [],
        createBasePathStyles(
            COLORS.PATHS.LINE,
            isClosed ? 0.3 : 1,
            6,
            isBold ? 16 : 8,
        ),
    );
};

/**
 * Creates a set of styled features for a common path
 */
const createCommonPathFeatures = (
    coordinates: number[][],
    id: string,
    color: string,
    isClosed: boolean,
    isBold: boolean,
    pathDirection?: PathDirection,
    zoom?: number,
): Feature<Geometry>[] => {
    // Keep original color but reduce opacity to 30% for closed paths
    return createBasePathFeatures(
        coordinates,
        FeatureType.Path,
        id,
        [], // No text labels for closed paths
        createBasePathStyles(color, isClosed ? 0.3 : 1, 6, isBold ? 16 : 8),
        pathDirection,
        zoom,
    );
};

/**
 * Creates a set of styled base featureas for an aoi using the given style
 */
const createBaseAoiFeatures = (
    coordinates: number[][],
    type: string,
    id: string,
    aoiStyle: Style | Style[],
): Feature<Geometry>[] => {
    const features = [];
    const aoiFeature = new Feature({
        geometry: new Polygon([coordinates]),
    });
    aoiFeature.set(OBJECT_TYPE, type);
    aoiFeature.set(OBJECT_ID, id);
    aoiFeature.setStyle(aoiStyle);
    features.push(aoiFeature);
   
    return features;
};

/**
 * Creates a set of styled features for a common aoi
 */
const createCommonAoiFeatures = (
    coordinates: number[][],
    id: string,
    color: string,
    
): Feature<Geometry>[] => {
    return createBaseAoiFeatures(
        coordinates,
        FeatureType.Aoi,
        id,
        createBaseAoisStyles(color),
    );
};

/**
 * Post a message to the host app
 */
const postMessage = (msg: string) => {
    if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(msg);
    }
};

export {
    getRenderScale,
    setRenderScale,
    createEmptyVectorSource,
    createEmptyVectorLayer,
    createIconClusterLayer,
    createGpsIndicatorFeature,
    createSnowmobilingIconFeature,
    createCommonPathIconFeature,
    createPathIconFeatures,
    createCommonPoiIconFeature,
    createPoiEventConnectorFeature,
    createPoiEventRowIconFeature,
    createPoiEventCountBadgeFeature,
    POI_EVENT_GROUP_PROPERTY,
    POI_LAYER_RENDER_BUFFER,
    createTemperaturePoiIconFeature,
    createCommonAoiIconFeature,
    createEndcapIconFeature,
    createSnowmobilePathFeatures,
    createCommonPathFeatures,
    postMessage,
    createCommonAoiFeatures
};